// PWA install prompt — singleton that captures the browser's
// `BeforeInstallPromptEvent` BEFORE React mounts, so even on the very
// first paint we already know whether install is available. Components
// subscribe via `subscribe()` to re-render when the state flips.
//
// Why not just use a React state? Because the `beforeinstallprompt` event
// can fire before any component mounts — if we wait for React, we miss it
// entirely on returning visits. Capturing at module-load time fixes that.
let deferred = null;
let installed = false;
const listeners = new Set();
function notify() {
    for (const fn of listeners)
        fn();
}
if (typeof window !== "undefined") {
    // Browser fires this when its install heuristic is satisfied AND the user
    // isn't already installed. We grab the event and prevent the mini-infobar
    // so we can render OUR own install button instead.
    window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        deferred = e;
        notify();
    });
    // Fires after a successful install (user accepted our or browser's prompt).
    // We use this to hide the install button immediately so nothing nags an
    // already-installed user.
    window.addEventListener("appinstalled", () => {
        installed = true;
        deferred = null;
        notify();
    });
}
/** True if the page is being rendered inside an installed PWA (standalone
 *  mode). On iOS we also check `navigator.standalone` since iOS Safari
 *  doesn't set `display-mode: standalone` on older versions. */
export function isStandalone() {
    if (typeof window === "undefined")
        return false;
    if (window.matchMedia("(display-mode: standalone)").matches)
        return true;
    const nav = window.navigator;
    return !!nav.standalone;
}
/** True on iOS Safari (no install API). We render an instruction sheet
 *  instead of a real prompt, because Apple doesn't expose one. */
export function isIOS() {
    if (typeof navigator === "undefined")
        return false;
    return /iPhone|iPad|iPod/i.test(navigator.userAgent)
        && !window.MSStream;
}
/** Has the browser already given us a deferred install event? */
export function canPromptInstall() {
    return !installed && !isStandalone() && deferred !== null;
}
/** True if we should still show SOMETHING (button or iOS hint) — i.e.
 *  the app isn't installed AND we're not running standalone. The button's
 *  behaviour differs (real prompt vs iOS guide) based on platform. */
export function shouldOfferInstall() {
    return !installed && !isStandalone();
}
/** Re-render hook. Components call this in `useEffect` to subscribe to
 *  install-state changes; returns an unsubscribe function. */
export function subscribe(fn) {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
}
/** Trigger the captured native prompt. Resolves to the user's choice or
 *  `null` if there was no captured event. After the prompt resolves the
 *  event is consumed (can't be re-prompted) — listeners are notified so
 *  the button can hide itself. */
export async function triggerInstall() {
    if (!deferred)
        return null;
    try {
        await deferred.prompt();
        const choice = await deferred.userChoice;
        deferred = null;
        notify();
        return choice.outcome;
    }
    catch {
        // Some browsers throw if `prompt()` is called outside a user gesture.
        deferred = null;
        notify();
        return null;
    }
}
